import { CheckCircle, ScanLine } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal } from '@/ui/primitives';
import { useVarreduraStatus } from '../api';

interface Props {
  open: boolean;
  initialTotal: number;
  onClose: () => void;
  onFinished: () => void;
}

export function VarreduraProgressModal({ open, initialTotal, onClose, onFinished }: Props) {
  const { data } = useVarreduraStatus(open);
  const [log, setLog] = useState<string[]>([]);
  const lastAtualRef = useRef(0);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setLog([]);
      lastAtualRef.current = 0;
    }
  }, [open]);

  useEffect(() => {
    if (!data?.em_andamento) return;
    if (data.atual > lastAtualRef.current && data.nome_atual) {
      setLog((prev) => [...prev, data.nome_atual!]);
      lastAtualRef.current = data.atual;
    }
  }, [data]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log]);

  useEffect(() => {
    if (!data || !open) return;
    if (!data.em_andamento && data.atual > 0 && data.atual >= data.total) {
      onFinished();
    }
  }, [data, open, onFinished]);

  const total = data?.total || initialTotal || 0;
  const atual = data?.atual || 0;
  const pct = total > 0 ? Math.min(100, (atual / total) * 100) : 0;
  const isFinished = !data?.em_andamento && atual > 0 && atual >= total;
  const nomeAtual = data?.nome_atual;

  return (
    <Modal
      open={open}
      onOpenChange={(v) => { if (!v && isFinished) onClose(); }}
      title={isFinished ? 'Varredura concluída' : 'Varredura em andamento'}
      size="md"
      footer={isFinished ? <Button variant="primary" onClick={onClose}>Fechar</Button> : undefined}
    >
      <div className="flex flex-col gap-4">
        {/* status + contador */}
        <div className="flex items-center gap-3">
          {isFinished ? (
            <CheckCircle size={28} strokeWidth={1.5} className="text-ok shrink-0" />
          ) : (
            <ScanLine size={28} strokeWidth={1.5} className="text-accent shrink-0 animate-pulse" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text">
              {isFinished ? 'Carteira processada com sucesso' : 'Processando carteira…'}
            </p>
            {nomeAtual && !isFinished && (
              <p className="text-xs text-text-2 truncate">{nomeAtual}</p>
            )}
          </div>
          <span className="text-sm font-mono tabular-nums text-text-2 shrink-0">
            {atual} / {total || '…'}
          </span>
        </div>

        {/* barra de progresso */}
        <div className="flex flex-col gap-1">
          <div className="h-2 rounded-full bg-surface overflow-hidden">
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs text-text-muted">{Math.round(pct)}% concluído</span>
        </div>

        {/* log de clientes processados */}
        {(log.length > 0 || !data) && (
          <div className="flex flex-col gap-1.5">
            <span
              className="text-text-muted font-semibold uppercase"
              style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
            >
              Clientes processados
            </span>
            <div className="max-h-52 overflow-y-auto flex flex-col gap-0.5 pr-1">
              {log.length === 0 && !isFinished && (
                <p className="text-xs text-text-muted">Iniciando…</p>
              )}
              {log.map((nome, i) => (
                <div key={i} className="flex items-center gap-2 py-[3px]">
                  <CheckCircle size={11} strokeWidth={1.5} className="text-ok shrink-0" />
                  <span className="text-xs text-text-2 truncate">{nome}</span>
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          </div>
        )}

        {(data?.erros ?? 0) > 0 && (
          <p className="text-xs text-danger">{data!.erros} erro(s) encontrado(s)</p>
        )}
      </div>
    </Modal>
  );
}
